import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SalesTab = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');

  useEffect(() => {
    if (user?.id) {
      loadSales();
    }
  }, [user]);

  useEffect(() => {
    filterSales();
  }, [sales, statusFilter, paymentFilter]);

  const loadSales = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('provider_sales')?.select(`
          *,
          quote:provider_quotes(quote_number, customer_name, project_type),
          customer:crm_customers(customer_name, customer_email, customer_phone)
        `)?.eq('provider_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setSales(data || []);
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSales = () => {
    let filtered = [...sales];

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((s) => s?.progress_status === statusFilter);
    }

    if (paymentFilter !== 'all') {
      filtered = filtered?.filter((s) => s?.payment_status === paymentFilter);
    }

    setFilteredSales(filtered);
  };

  const handleStatusUpdate = async (saleId, newStatus) => {
    try {
      const { error } = await supabase?.from('provider_sales')?.update({ progress_status: newStatus })?.eq('id', saleId)?.eq('provider_id', user?.id);

      if (error) throw error;
      loadSales();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePaymentUpdate = async (saleId, newPaymentStatus) => {
    try {
      const { error } = await supabase?.from('provider_sales')?.update({ payment_status: newPaymentStatus })?.eq('id', saleId)?.eq('provider_id', user?.id);

      if (error) throw error;
      loadSales();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const getProgressBadge = (status) => {
    const styles = {
      gepland: 'bg-info/10 text-info border-info/20',
      in_uitvoering: 'bg-warning/10 text-warning border-warning/20',
      afgerond: 'bg-success/10 text-success border-success/20',
    };
    const labels = {
      gepland: 'Gepland',
      in_uitvoering: 'In Uitvoering',
      afgerond: 'Afgerond',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[status]}`}>
        {labels?.[status]}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      niet_betaald: 'bg-error/10 text-error border-error/20',
      gedeeltelijk_betaald: 'bg-warning/10 text-warning border-warning/20',
      volledig_betaald: 'bg-success/10 text-success border-success/20',
    };
    const labels = {
      niet_betaald: 'Niet Betaald',
      gedeeltelijk_betaald: 'Gedeeltelijk Betaald',
      volledig_betaald: 'Volledig Betaald',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[status]}`}>
        {labels?.[status]}
      </span>
    );
  };

  const statusOptions = [
    { value: 'all', label: 'Alle Statussen' },
    { value: 'gepland', label: 'Gepland' },
    { value: 'in_uitvoering', label: 'In Uitvoering' },
    { value: 'afgerond', label: 'Afgerond' },
  ];

  const paymentOptions = [
    { value: 'all', label: 'Alle Betalingen' },
    { value: 'niet_betaald', label: 'Niet Betaald' },
    { value: 'gedeeltelijk_betaald', label: 'Gedeeltelijk Betaald' },
    { value: 'volledig_betaald', label: 'Volledig Betaald' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Verkooptracking</h2>
          <p className="text-muted-foreground mt-1">Beheer installaties en betalingen</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter op voortgang"
          />
          <Select
            options={paymentOptions}
            value={paymentFilter}
            onChange={setPaymentFilter}
            placeholder="Filter op betaling"
          />
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Klant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bedrag
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Installatie Datum
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Voortgang
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Betaling
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredSales?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                    Geen verkopen gevonden
                  </td>
                </tr>
              ) : (
                filteredSales?.map((sale) => (
                  <tr key={sale?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {sale?.customer?.customer_name || sale?.quote?.customer_name || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sale?.customer?.customer_email || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm text-foreground">{sale?.quote?.quote_number || '-'}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {sale?.quote?.project_type || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-foreground">
                      €{parseFloat(sale?.sale_amount)?.toLocaleString('nl-NL')}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {sale?.installation_date
                        ? new Date(sale?.installation_date)?.toLocaleDateString('nl-NL')
                        : '-'}
                    </td>
                    <td className="px-4 py-4">{getProgressBadge(sale?.progress_status)}</td>
                    <td className="px-4 py-4">{getPaymentBadge(sale?.payment_status)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        {sale?.progress_status !== 'afgerond' && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() =>
                              handleStatusUpdate(
                                sale?.id,
                                sale?.progress_status === 'gepland' ? 'in_uitvoering' : 'afgerond'
                              )
                            }
                          >
                            {sale?.progress_status === 'gepland' ? 'Start' : 'Voltooi'}
                          </Button>
                        )}
                        {sale?.payment_status !== 'volledig_betaald' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handlePaymentUpdate(
                                sale?.id,
                                sale?.payment_status === 'niet_betaald' ?'gedeeltelijk_betaald' :'volledig_betaald'
                              )
                            }
                          >
                            Betaling
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesTab;